import { Link } from '@buoyhealth/common.buoy-button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Grid from '@mui/material/Grid';
import { Fragment, useCallback, useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import useImpressionTracking from 'src/hooks/useImpressionTracking';
import { trackTreatmentCardViewed, trackTreatmentCtaClicked } from 'src/interviews/analytics';
import { Paragraph } from 'src/interviews/components/Layout/Embedded';
import { trackTreatmentsForYou } from '../../analytics';
import Card from '../Card';
import TreatmentSupplierIconComponent from '../TreatmentSupplierIcon';

import type {
  IInterviewResults,
  MiniInterviewType,
  TreatmentSupplier,
} from 'src/packages/polaris/types';

type Props = {
  treatments: TreatmentSupplier[];
  interviewToken: string;
  interviewType: MiniInterviewType;
  isLoading: boolean;
  result?: IInterviewResults;
};

type TreatmentCardProps = {
  index: number;
  treatment: TreatmentSupplier;
  containerClassName?: string;
  onClick: (args: {
    impressionId: string;
    buttonLabel: string;
    url?: string;
    interviewType: MiniInterviewType;
  }) => void;
  onViewed: (args: {
    impressionId: string;
    order: number;
    url?: string;
    interviewType: MiniInterviewType;
  }) => void;
  interviewType: MiniInterviewType;
};

export function TreatmentCard(props: TreatmentCardProps) {
  const { index, treatment, onClick, onViewed, interviewType } = props;
  const url = treatment?.modalUrl || treatment?.url;

  const impressionId = useMemo(() => uuid(), [url]);

  const impressionRef = useImpressionTracking(() => {
    onViewed({ impressionId, order: index, url, interviewType });
  });

  const handleClicked = useCallback(() => {
    onClick({ impressionId, buttonLabel: treatment.cta_button_label, url, interviewType });
  }, [impressionId, interviewType, onClick, treatment.cta_button_label, url]);

  if (!url) {
    return null;
  }

  return (
    <Card
      className="bp-bg-orange-0"
      cta={
        <Link className="bp-mt-auto bp-flex bp-w-full bp-items-center bp-justify-between bp-rounded-3xl bp-bg-[#FFC95F] bp-px-6 bp-py-2 !bp-no-underline">
          <Paragraph className="!bp-text-sm">{treatment.cta_button_label}</Paragraph>
          <ArrowForwardIcon className="bp-h-4 bp-w-4 bp-fill-[#323232]" />
        </Link>
      }
      innerRef={impressionRef}
      href={url}
      onClick={handleClicked}
      key={treatment.header}
      subtitle={
        treatment.modal_header_intro && (
          <Paragraph className="!bp-text-xs bp-uppercase">{treatment.modal_header_intro}</Paragraph>
        )
      }
      title={treatment.header}
      containerClassName={props.containerClassName}
    >
      <div className="bp-flex bp-flex-col bp-gap-4">
        <Paragraph className="bp-font-secondary !bp-text-sm bp-leading-secondary bp-tracking-[-0.64px]">
          {treatment.subheader}
        </Paragraph>
        {!!treatment?.bullet_points?.length && (
          <Paragraph className="bp-grid bp-grid-cols-[24px_1fr] bp-gap-2 bp-font-primary !bp-text-sm bp-leading-[120%]">
            {treatment?.bullet_points?.map((point, index) => (
              <Fragment key={point.text}>
                <TreatmentSupplierIconComponent
                  key={point.icon}
                  type={point.icon}
                  iconClassName="bp-fill-orange-30"
                  color="#f9ae0f"
                >
                  {index + 1}
                </TreatmentSupplierIconComponent>
                <Paragraph className="bp-flex bp-items-center !bp-text-sm">{point.text}</Paragraph>
              </Fragment>
            ))}
          </Paragraph>
        )}
      </div>
    </Card>
  );
}

const TreatmentsForYou = (props: Props) => {
  const { treatments = [], interviewToken, interviewType, isLoading, result } = props || {};

  const treatmentsForYou = treatments.filter((treatment) => treatment?.modalUrl || treatment?.url);

  useEffect(() => {
    if (!isLoading) {
      trackTreatmentsForYou(interviewToken || '', interviewType, treatmentsForYou);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewToken, interviewType, isLoading, treatmentsForYou?.length]);

  if (!treatmentsForYou?.length) {
    return null;
  }

  return (
    <Grid item className="bp-w-full">
      <Paragraph className="!bp-mb-2 !bp-font-bold">Treatment for you</Paragraph>
      <Paragraph className="!bp-mb-3 bp-text-base">
        {result?.alarm
          ? 'The below treatment providers can help clarify the urgency of your situation.'
          : 'Based on your answers, the below treatments are most relevant to your scenario.'}
      </Paragraph>

      <div className="bp-no-scrollbar bp-flex bp-gap-3 bp-overflow-x-scroll">
        {treatmentsForYou.map((treatment, index) => (
          <TreatmentCard
            key={treatment?.modalUrl || treatment?.url}
            index={index}
            treatment={treatment}
            onClick={trackTreatmentCtaClicked}
            onViewed={trackTreatmentCardViewed}
            interviewType={interviewType}
            containerClassName="bp-max-w-[343px]"
          />
        ))}
      </div>
    </Grid>
  );
};

export default TreatmentsForYou;
